import { createRoot } from "react-dom/client";
import '@utils/setAssetPath';
import '@utils/polyfills/scheduling';
import '@services/conditionEngine/evaluators';

import { isEmpty, merge } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';

import App from '@components/App';

import MediaQueryWatcher from '@models/mediaQueryWatcher';
import NavigationButtonsNormalizer from '@models/navigationButtonsNormalizer';
import QuestionNormalizer from '@models/questionNormalizer';
import TouchDetector from '@models/touchDetector';

import { getInitialState as getCookieBannerInitialState } from '@redux/reducers/cookieBanner';
import { getInitialState as getPageSettingsInitialState } from '@redux/reducers/pageSettings';
import { getInitialState as getSurveyStatusInitialState } from '@redux/reducers/surveyStatus';
import createStoreWithInitialState from '@redux/store';

import { surveyIsReady } from '@services/api/events';
import { setStore } from '@services/api/surveyMethods';
import { bootstrapDebuggerOverrides } from '@services/debugger/utils';
import {
  isNormalizedLangAttributeEnabled,
  isSurveyAPIEnabled,
  setFeatureFlags
} from '@services/featureFlags';
import FieldsService from '@services/fields';
import IPCService from '@services/inPageConditioning';
import { createProvider } from '@services/surveyDataProvider';
import { isTelemetryServiceEnabled } from '@services/telemetry/utils';
import { defineTranslations } from '@services/translations';
import ValidationsService from '@services/validations';

import getAllBlocks from '@utils/getAllBlocks';
import parseBoolProperties from '@utils/parseBoolProperties';
import { shouldEnableSurveyDataOverrides, surveyDataOverrides } from '@utils/surveyDataOverrides';
import normalizeLangAttribute from '@utils/normalizeLangAttribute';
import JQueryDetector from '@services/telemetry/detector-jquery';

if (!isEmpty(surveyDataOverrides) && shouldEnableSurveyDataOverrides()) {
  const newSurveyData = merge(window.surveyData, surveyDataOverrides);
  window.surveyData = newSurveyData;
}

bootstrapDebuggerOverrides();

const surveyData = parseBoolProperties(window.surveyData || {});
const questionBlocks = getAllBlocks(surveyData);
defineTranslations(window.surveyData.translations);
setFeatureFlags(window.surveyData);
const questionNormalizer = new QuestionNormalizer();
const navButtonsNormalizer = new NavigationButtonsNormalizer();

if (isNormalizedLangAttributeEnabled()) {
  normalizeLangAttribute(window.surveyData?.surveyLanguage);
}

// Deprecation pending: usage of surveyData variable. Start using surveyDataStore instead
let normalizedData = questionNormalizer.normalize({ rawData: surveyData, questionBlocks });
normalizedData = navButtonsNormalizer.normalize({ rawData: surveyData, normalizedData });
normalizedData.pageSettings = getPageSettingsInitialState(surveyData);
normalizedData.surveyStatus = getSurveyStatusInitialState();

// New store slices are created from survey data store **/
const surveyDataStore = createProvider(
  questionBlocks,
  surveyData.cookieNotice,
  surveyData.cookieConfiguration
);
normalizedData.cookieBanner = getCookieBannerInitialState(surveyDataStore.cookieBanner);

const store = createStoreWithInitialState(normalizedData);
IPCService.setStore(store).setFieldsService(FieldsService);
FieldsService.setStore(store).setIPCService(IPCService);
ValidationsService.setStore(store).init();

if (isTelemetryServiceEnabled()) {
  JQueryDetector.init();
}

MediaQueryWatcher.watch(store);
const $survey = document.getElementById('survey');
TouchDetector.detectAndAddHasTouchClassNameToDomNode($survey);

const root = createRoot($survey);
root.render(<App questionBlocks={questionBlocks} surveyData={surveyData} store={store} />);

requestIdleCallback(() => {
  if (isSurveyAPIEnabled()) {
    setStore(store);
    surveyIsReady();
  }

  if (isTelemetryServiceEnabled()) {
    import('@services/telemetry').then(({ default: TelemetryService }) => {
      TelemetryService.initializeSession();
      // Measurements
      TelemetryService.markDOMInteractive();
      TelemetryService.markBrandingCSSLoadTime();
      TelemetryService.markJQueryLoadTime();
      TelemetryService.sendPageLoadMeasurement();
      // Events
      TelemetryService.trackJQueryUsage();
      TelemetryService.trackBrowserCacheUsage();
    });
  }
});
